<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { useBrxmData } from '../../composables/useBrxmData'
import { getLinkTarget } from '../../utils/linkTarget'
import Drawer from '../Drawer/Drawer.vue'
import { EClickElement } from '@/cms-ui/utils/dataLayer'
import { VIconButtonVariants } from '@/ui/plushComponents/VIconButton/types'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { useClickTracking, useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'

const props = defineProps<IBrxmProps>()

const data = useBrxmData(props, 'FOOTER')
const { bottomItems, copyright, logo, socialMediaItems, topItems } = data.value
const currentYear = new Date().getFullYear()
const footerRef = useTemplateRef('footerRef')

useVisibilityTracking(footerRef, 'Footer', 'Footer')
</script>

<template>
    <footer
        ref="footerRef"
        class="md:m-base space-footer flex justify-center"
    >
        <div class="bg-neutral-secondary p-base md:p-md md:rounded-container-md w-full max-w-[1440px]">
            <div class="mx-auto max-w-[1224px] md:flex md:flex-wrap md:justify-between">
                <nav class="gap-md md:order-1 md:flex md:w-full lg:w-auto">
                    <a
                        v-for="item in topItems"
                        :key="item.label"
                        :href="item.url"
                        :target="getLinkTarget(item)"
                        class="py-base text-comp-high-contrast label-lg active:text-brand-accent-on-dark block hover:underline lg:max-h-[56px]"
                        @click="
                            useClickTracking({
                                label: item.label,
                                clickPath: item.url || '',
                                clickElement: EClickElement.LINK,
                                componentName: 'footer',
                                componentCategory: 'footer',
                            })
                        "
                    >
                        {{ item.label }}
                    </a>
                </nav>
                <nav
                    class="my-md md:gap-xs md:mt-lg ld:w-auto flex justify-between md:order-3 md:my-0 md:w-1/2 md:justify-start lg:order-2 lg:mt-0 lg:justify-end lg:gap-0"
                >
                    <VIconButton
                        v-for="item in socialMediaItems"
                        :key="item.name"
                        :href="item.url"
                        :variant="VIconButtonVariants.plain"
                        size="LARGE"
                        :icon="item.icon"
                        :data-test="`footer-social-${item.icon}`"
                        class="inline-block"
                        @click="
                            useClickTracking({
                                label: item.icon,
                                clickPath: item.url || '',
                                clickElement: EClickElement.ICON,
                                componentName: 'footer',
                                componentCategory: 'footer',
                            })
                        "
                    />
                </nav>
                <nav class="md:gap-x-md lg:-mt-lg md:order-2 md:flex md:w-full md:flex-wrap lg:order-4">
                    <a
                        v-for="item in bottomItems"
                        :key="item.label"
                        :href="item.url"
                        :target="getLinkTarget(item)"
                        class="py-base text-comp-high-contrast label-sm active:text-brand-accent-on-dark block px-0 hover:underline"
                        @click="
                            useClickTracking({
                                label: item.label,
                                clickPath: item.url || '',
                                clickElement: EClickElement.LINK,
                                componentName: 'footer',
                                componentCategory: 'footer',
                            })
                        "
                    >
                        {{ item.label }}
                    </a>
                    <a
                        id="ot-sdk-btn"
                        href="#"
                        class="ot-sdk-show-settings !py-base !text-comp-high-contrast !label-sm active:!text-brand-accent-on-dark !block !px-0 hover:!underline"
                        @click="
                            useClickTracking({
                                label: 'Ustawienia',
                                clickPath: '#',
                                clickElement: EClickElement.LINK,
                                componentName: 'footer',
                                componentCategory: 'footer',
                            })
                        "
                    >
                        Ustawienia
                    </a>
                </nav>
                <div
                    class="mt-md md:gap-base lg:gap-lg flex items-end justify-between md:order-4 md:mt-0 md:w-1/2 md:justify-end lg:order-3 lg:w-auto lg:flex-col-reverse"
                >
                    <p
                        v-html-gpp="`${copyright} ${currentYear}`"
                        class="label-sm text-neutral-tertiary"
                    />
                    <a :href="logo.url">
                        <img
                            src="../../assets/images/logo-plush.svg"
                            alt="Logo PLUSH"
                        />
                    </a>
                </div>
            </div>
        </div>
    </footer>

    <Drawer />
</template>

<style scoped>
* {
  @apply focus:outline-focus;
}

#ot-sdk-btn.ot-sdk-show-settings {
  border: unset;
  background-color: unset;
}
</style>
